import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { useNavigate } from 'react-router-dom';
import { getPeakHourTreeData, workspaceGetEnv, fetchSectorList } from 'service/service';
import moment from 'moment';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import LoaderComponent from 'components/Loader';
import PageWrapper from 'components/Common/PageWraper';
import FiltersBar from './FilterBar';
import { extractDatesFromNode, findSelectedNode, currencyFormatter } from 'utils/common-util';
import {
    commonFetchSectors,
    useAdbFilters,
    useAdbHierarchyTable,
    syncEnvWithOptions,
    resolveTreeNodeCount
} from 'utils/adb-common-util';

const getPeakHourColumns = (onExpand: (id: string) => void, onRowClick: (row: any) => void, totalCount: number) => [
    {
        key: 'time_range',
        name: 'Time Range',
        formatter({ row, isCellSelected }: any) {
            const hasChildren = row.children && row.children.length > 0;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', pl: row.level * 2 }}>
                    {hasChildren && (
                        <CellExpanderFormatter
                            isCellSelected={isCellSelected}
                            expanded={row.isExpanded}
                            onCellExpand={() => onExpand(row.id)}
                        />
                    )}
                    <span>{row.time_range}</span>
                </Box>
            );
        },
        minWidth: 200
    },
    {
        key: 'cost',
        name: 'Cost',
        formatter({ row }: any) {
            const val = row.count ?? 0;
            return (
                <Box sx={{ cursor: 'pointer', color: '#4fc3f7', textAlign: 'left' }} onClick={() => onRowClick(row)}>
                    {currencyFormatter.format(val)}
                </Box>
            );
        },
        minWidth: 180
    },
    {
        key: 'percent_of_total',
        name: '% of Total',
        formatter({ row }: any) {
            const pct = totalCount > 0 ? ((row.count / totalCount) * 100).toFixed(2) : '0.00';
            return <Box sx={{ textAlign: 'left' }}>{pct}%</Box>;
        },
        minWidth: 150
    }
];

const PRESERVE_FILTER_KEY = 'peak_hour_filters';

const PeakHourDetails = () => {
    const navigate = useNavigate();

    const {
        startDate, setStartDate, endDate, setEndDate,
        env, setEnv, sector, setSector, sectorName, setSectorName,
        location, setLocation, selectedDateInString, setSelectedDateInString,
        apiDateRangeKey, updateHistoryState, handleDateSelect, restoredRef, envRef, initialEnvAppliedRef, navigationState
    } = useAdbFilters(PRESERVE_FILTER_KEY);

    const [loader, setLoader] = useState(false);
    const [apiData, setApiData] = useState<any[]>([]);
    const [totalCost, setTotalCost] = useState<number>(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
    const [sectorList, setSectorList] = useState<string[]>([]);

    const { tableData, toggleExpandRow, paginationData, rowsRef } = useAdbHierarchyTable(apiData, pageSize);

    useEffect(() => {
        commonFetchSectors(fetchSectorList, setSectorList);
    }, []);

    useEffect(() => {
        const getEnvFetch = async (loc: string) => {
            try {
                const res = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptions(options);
                syncEnvWithOptions(options, initialEnvAppliedRef, envRef, navigationState?.env, setEnv);
            } catch (err) {
                console.error(err);
            }
        };
        getEnvFetch(location);
    }, [location, navigationState?.env]);

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true);
            try {
                const filters = [];
                if (navigationState?.workspaceId) {
                    filters.push({ operator: "AND", field: "workspace_name", value: navigationState.workspaceId });
                }
                const resp = await getPeakHourTreeData({
                    startDate,
                    endDate,
                    env,
                    sector,
                    location,
                    selectedDateInString: apiDateRangeKey,
                    page,
                    limit: pageSize,
                    filters
                });

                if (resp?.success && Array.isArray(resp.data)) {
                    setApiData(resp.data);
                    const total = resp.data.reduce((sum: number, item: any) => sum + resolveTreeNodeCount(item[Object.keys(item)[0]]), 0);
                    setTotalCost(total);
                } else {
                    setApiData([]);
                    setTotalCost(0);
                }
            } catch (error) {
                console.error(error);
                setApiData([]);
                setTotalCost(0);
            } finally {
                setLoader(false);
                if (restoredRef.current) {
                    sessionStorage.removeItem(PRESERVE_FILTER_KEY);
                    restoredRef.current = false;
                }
            }
        };
        fetchData();
    }, [startDate, endDate, env, sector, location, apiDateRangeKey, page, pageSize, navigationState?.workspaceId]);

    const handleRowClick = useCallback((row: any) => {
        let type: 'year' | 'month' | 'week' | 'day' = 'year';
        if (row.id.includes('day')) type = 'day';
        else if (row.id.includes('week')) type = 'week';
        else if (row.id.includes('month')) type = 'month';

        const found = findSelectedNode(apiData, type, row.time_range, row.id);
        const navStateBase: any = {
            dateLabel: row.time_range,
            filterType: navigationState?.fromActiveWorkspaceKPI ? 'workspace' : 'peak_hour',
            resultState: 'FAILED',
            type, startDate, endDate, env, sector, location, selectedDateInString,
            fromActiveWorkspaceKPI: navigationState?.fromActiveWorkspaceKPI || false
        };

        if (navigationState?.fromActiveWorkspaceKPI && navigationState?.workspaceName) {
            navStateBase.workspaceName = navigationState.workspaceName;
            navStateBase.workspaceId = navigationState.workspaceId;
        }

        if (found?.kind) {
            const dates = extractDatesFromNode(found.node);
            if (found.kind === 'day' && found.node?.date) {
                const d = moment(found.node.date, 'YYYY-MM-DD').format('YYYY-MM-DD');
                navStateBase.startDate = d;
                navStateBase.endDate = d;
                navStateBase.selectedDateInString = 'customDateRange';
            } else if (dates.length > 0) {
                const moments = dates.map((ds: string) => moment(ds, 'YYYY-MM-DD'));
                navStateBase.startDate = moment.min(moments).format('YYYY-MM-DD');
                navStateBase.endDate = moment.max(moments).format('YYYY-MM-DD');
                navStateBase.selectedDateInString = 'customDateRange';
            }
            setPage(1);
            rowsRef.current = [];
        }

        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify({ startDate, endDate, selectedDateInString }));
        navigate('/databricksADB/peakHourDetails', { state: navStateBase });
    }, [navigate, apiData, startDate, endDate, env, sector, location, selectedDateInString, navigationState, rowsRef]);

    const memoizedColumns = useMemo(() => getPeakHourColumns(toggleExpandRow, handleRowClick, totalCost), [toggleExpandRow, handleRowClick, totalCost]);

    const handlePageChange = (_event: any, value: number) => setPage(value);
    const handlePageSizeChange = (event: any) => { setPageSize(Number(event.target.value)); setPage(1); };

    const renderTableContent = () => {
        if (loader) {
            return <LoaderComponent pt={20} />;
        }
        if (tableData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 10, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>No Data Available</Box>
                    <Box sx={{ fontSize: '0.9rem', color: '#999' }}>No data found for the selected filters.</Box>
                </Box>
            );
        }
        return (
            <CustomizedDataGrid
                title={`Most Active Workspace (${currencyFormatter.format(totalCost)})`}
                tableColumns={memoizedColumns}
                tableData={tableData}
                tableType="cost-details-ver"
                defaultColumnNames={['Time Range', 'Cost', '% of Total']}
                page={page} pageSize={pageSize}
                paginationData={paginationData}
                listLength={tableData.length}
                handlepageSizeChange={handlePageSizeChange}
                handlePageChange={handlePageChange}
                isDateRangeFilter={false} isDisabled={false} isFetching={loader}
            />
        );
    };

    return (
        <PageWrapper>
            <PageHeader
                title={[
                    {
                        text: 'Peak Hour Analysis',
                        link: '/databricksADB/jobsADB',
                        state: { startDate, endDate, env, sector, location, selectedDateInString, activeTab: 1 }
                    },
                    {
                        text: 'Most Active Workspace'
                    }
                ]}
                isPepwise2Breadcrumb={true}
            />

            <FiltersBar
                location={location} setLocation={setLocation}
                env={env} setEnv={setEnv}
                envOptions={envOptions} setEnvOptions={setEnvOptions}
                startDate={startDate} endDate={endDate}
                setStartDate={setStartDate} setEndDate={setEndDate}
                handleDateSelect={handleDateSelect}
                selectedDateInString={selectedDateInString}
                setSelectedDateInString={setSelectedDateInString}
                sectorName={sectorName}
                setSectorName={(v: any) => {
                    const resolved = typeof v === 'function' ? v(sectorName) : v;
                    setSectorName(resolved);
                    setSector(resolved);
                    setEnv('both');
                }}
                updateHistoryState={updateHistoryState}
                sectorOptionsList={sectorList}
                useHeaderFilters
            />
            {renderTableContent()}
        </PageWrapper>
    );
};

export default PeakHourDetails;